import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import firebase from '../store/firebase'
import { calculateTestPassed, calculateLCEarned } from '../utils/test_utils'

const headerStyle = {
  fontWeight: 'bold',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #ddd'
}

const cellStyle = {
  padding: '8px',
  borderBottom: '1px solid #eee',
  fontSize: '12px'
}

const sectionTitle = {
  fontSize: '16px',
  fontWeight: 'bold',
  margin: '10px 0 6px'
}

function MyTestResults({ userdata, history }) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false))
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(max-width: 640px)')
    const handler = (e) => setIsMobile(e.matches)
    if (mql.addEventListener) mql.addEventListener('change', handler)
    else if (mql.addListener) mql.addListener(handler)
    setIsMobile(mql.matches)
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler)
      else if (mql.removeListener) mql.removeListener(handler)
    }
  }, [])

  useEffect(() => {
    if (!userdata) return
    const companyID = userdata.real_companyID || userdata.companyID
    const personid = userdata.userID
    const ref = firebase.database.ref(`userTestResults/${companyID}/${personid}`)

    const handle = ref.on('value', (snap) => {
      const val = snap.val() || {}
      const list = Object.keys(val).map((surveyid) => ({ surveyid, ...val[surveyid] }))
      setRows(list)
      setLoading(false)
    })

    return () => ref.off('value', handle)
  }, [userdata])

  const passedRows = useMemo(() => {
    return rows
      .map((r) => {
        const total = parseInt(r.totalAnswered || r.qlen || 0)
        const score = parseInt(r.testscore || 0)
        const passed = (typeof r.testPassed === 'boolean')
          ? r.testPassed
          : calculateTestPassed(score, total, r.difficulty || 'basic')
        const percentage = total > 0 ? ((score / total) * 100).toFixed(1) : '0.0'
        const lc = typeof r.lcEarned === 'number'
          ? r.lcEarned
          : (passed ? calculateLCEarned(score, true, r.difficulty || 'basic') : 0)
        return {
          ...r,
          passed,
          percentage,
          lc
        }
      })
      .filter((r) => r.passed)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  }, [rows])

  const totals = useMemo(() => {
    const byCategory = {}
    const bySubCategory = {}
    let totalLC = 0
    passedRows.forEach((r) => {
      const cat = r.leadCategory || r.categoryType || 'general'
      const sub = r.leadSubCategory || ''
      const lc = Number(r.lc || 0)
      byCategory[cat] = (byCategory[cat] || 0) + lc
      if (sub) bySubCategory[sub] = (bySubCategory[sub] || 0) + lc
      totalLC += lc
    })
    return { byCategory, bySubCategory, totalLC }
  }, [passedRows])

  const cleanMainCategory = (name) => {
    if (!name) return ''
    const parts = String(name).split(/\s*[–—-]\s*/)
    return parts[0].trim()
  }

  const truncateText = (text, max = 90) => {
    const str = String(text || '')
    if (str.length <= max) return str
    return str.slice(0, max - 1) + '…'
  }

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  if (!userdata) {
    return <div style={{ padding: 16 }}>Please sign in to view your test results.</div>
  }

  // Mobile-first UI
  if (isMobile) {
    return (
      <div style={{ padding: '12px 10px 30px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 'bold' }}>My Tests</div>
            <div style={{ fontSize: 12, color: '#666' }}>{userdata.firstName} {userdata.lastName}</div>
          </div>
          <div>
            <button
              onClick={() => history && history.push('/my-resume')}
              style={{ backgroundColor: '#4CAF50', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
            >
              View Resume
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 11, color: '#777' }}>Tests Passed</div>
            <div style={{ fontSize: 20, fontWeight: 'bold' }}>{passedRows.length}</div>
          </div>
          <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 11, color: '#777' }}>Total LC</div>
            <div style={{ fontSize: 20, fontWeight: 'bold' }}>{totals.totalLC.toFixed(2)} LC</div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 6 }}>LC by Category</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.keys(totals.byCategory).length === 0 ? (
              <div style={{ color: '#777', fontSize: 12 }}>No data</div>
            ) : (
              Object.keys(totals.byCategory).map((cat) => (
                <div key={cat} style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', color: '#0f172a', borderRadius: 999, padding: '6px 10px', fontSize: 12 }}>
                  <b>{cleanMainCategory(cat)}</b>: {totals.byCategory[cat].toFixed(2)} LC
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 6 }}>Passed Tests</div>
          {loading ? (
            <div style={{ fontSize: 12, color: '#777' }}>Loading…</div>
          ) : passedRows.length === 0 ? (
            <div style={{ fontSize: 12, color: '#777' }}>No passed tests yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {passedRows.map((r) => {
                const fullTitle = (r.title || r.surveyname || r.surveyid)
                const showFull = !!expanded[r.surveyid]
                const title = showFull ? fullTitle : truncateText(fullTitle, 110)
                return (
                  <div key={r.surveyid} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
                    {String(fullTitle || '').length > 110 && (
                      <button onClick={() => toggleExpand(r.surveyid)} style={{ background: 'transparent', color: '#2563eb', border: 'none', padding: 0, marginTop: 4, fontSize: 12, cursor: 'pointer' }}>
                        {showFull ? 'See less' : 'See more'}
                      </button>
                    )}
                    <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                      {cleanMainCategory(r.leadCategory || r.categoryType || 'general')}{r.leadSubCategory ? ' → ' + r.leadSubCategory : ''}
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, background: '#f1f5f9', border: '1px solid #e5e7eb', padding: '2px 6px', borderRadius: 999 }}>Score {r.percentage || '0.0'}%</span>
                      <span style={{ fontSize: 11, background: '#f1f5f9', border: '1px solid #e5e7eb', padding: '2px 6px', borderRadius: 999 }}>{Number(r.lc || 0).toFixed(2)} LC</span>
                      <span style={{ fontSize: 11, background: '#f1f5f9', border: '1px solid #e5e7eb', padding: '2px 6px', borderRadius: 999 }}>{r.mediumType || 'content'}</span>
                      <span style={{ fontSize: 11, background: '#f1f5f9', border: '1px solid #e5e7eb', padding: '2px 6px', borderRadius: 999 }}>{r.timestamp ? new Date(r.timestamp).toLocaleDateString() : '-'}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '12px 10px 30px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 'bold' }}>ThinkLead resume</div>
          <div style={{ fontSize: 12, color: '#666' }}>{userdata.firstName} {userdata.lastName} • {userdata.companyName}</div>
        </div>
        <div>
          <button
            onClick={() => history && history.push('/my-resume')}
            style={{ backgroundColor: '#4CAF50', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
          >
            View Resume / Print Certificate
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 10, minWidth: 160 }}>
          <div style={{ fontSize: 11, color: '#777' }}>Tests Passed</div>
          <div style={{ fontSize: 22, fontWeight: 'bold' }}>{passedRows.length}</div>
        </div>
        <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 10, minWidth: 160 }}>
          <div style={{ fontSize: 11, color: '#777' }}>Total LC Earned</div>
          <div style={{ fontSize: 22, fontWeight: 'bold' }}>{totals.totalLC.toFixed(2)} LC</div>
        </div>
      </div>

      <div style={sectionTitle}>LC by Category</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {Object.keys(totals.byCategory).length === 0 ? (
          <div style={{ color: '#777', fontSize: 12 }}>No data</div>
        ) : (
          Object.keys(totals.byCategory).map((cat) => (
            <div key={cat} style={{ background: '#f1f8e9', border: '1px solid #c5e1a5', color: '#33691e', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
              <b>{cleanMainCategory(cat)}</b>: {totals.byCategory[cat].toFixed(2)} LC
            </div>
          ))
        )}
      </div>

      <div style={sectionTitle}>LC by Sub-category</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {Object.keys(totals.bySubCategory).length === 0 ? (
          <div style={{ color: '#777', fontSize: 12 }}>No data</div>
        ) : (
          Object.keys(totals.bySubCategory).map((sub) => (
            <div key={sub} style={{ background: '#eef6ff', border: '1px solid #dbeafe', color: '#1e3a8a', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
              <b>{sub}</b>: {totals.bySubCategory[sub].toFixed(2)} LC
            </div>
          ))
        )}
      </div>

      <div style={sectionTitle}>Passed Tests</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...cellStyle, ...headerStyle }}>Date</th>
              <th style={{ ...cellStyle, ...headerStyle, minWidth: 180 }}>Test Title</th>
              <th style={{ ...cellStyle, ...headerStyle }}>Category → Sub-category</th>
              <th style={{ ...cellStyle, ...headerStyle }}>Medium</th>
              <th style={{ ...cellStyle, ...headerStyle }}>Difficulty</th>
              <th style={{ ...cellStyle, ...headerStyle }}>Score %</th>
              <th style={{ ...cellStyle, ...headerStyle }}>LC Earned</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ ...cellStyle, textAlign: 'center' }}>Loading...</td></tr>
            ) : passedRows.length === 0 ? (
              <tr><td colSpan={7} style={{ ...cellStyle, textAlign: 'center', color: '#777' }}>No passed tests yet.</td></tr>
            ) : (
              passedRows.map((r) => (
                <tr key={r.surveyid}>
                  <td style={cellStyle}>{r.timestamp ? new Date(r.timestamp).toLocaleDateString() : '-'}</td>
                  <td style={cellStyle}>{(r.title || r.surveyname || r.surveyid || '').toString().length > 80 ? (r.title || r.surveyname || r.surveyid).toString().slice(0, 77) + '…' : (r.title || r.surveyname || r.surveyid)}</td>
                  <td style={cellStyle}>{`${cleanMainCategory(r.leadCategory || r.categoryType || 'general')}${r.leadSubCategory ? ' → ' + r.leadSubCategory : ''}`}</td>
                  <td style={cellStyle}>{r.mediumType || 'content'}</td>
                  <td style={cellStyle}>{(r.difficulty || 'basic')}</td>
                  <td style={cellStyle}>{r.percentage || '0.0'}%</td>
                  <td style={cellStyle}>{Number(r.lc).toFixed ? Number(r.lc).toFixed(2) : Number(r.lc).toString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  userdata: state.userDialog.userDetails[0] || null
})

export default connect(mapStateToProps)(MyTestResults)


